import $ from 'jquery';

// @types/select2 を使いたいが @types/jquery に依存していて、 @types/jquery が jquery 3.x 用なので
// jquery をアップデートするまでは必要な部分を抜き出して使う。
// https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/select2/index.d.ts

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Select2 {
  export interface QueryOptions {
    term?: string | undefined;
    page?: number | undefined;
  }
  export interface TranslationArg {
    input: string;
    minimum: number;
    maximum: number;
  }
  export interface OptionData {
    disabled: boolean;
    element: HTMLOptionElement;
    id: string;
    selected: boolean;
    text: string;
    title: string;

    loading?: undefined;
    children?: undefined;
  }
}

type CompanyQueryParam = {
  keyword: string;
  with_unpublished?: boolean;
};

type videoBundleCompanyQueryParam = {
  company_ids: string;
  keyword: string;
};

type CompanyQueryResult = {
  companies: { id: number; name: string }[];
};

type VideoChaptersResult = {
  video_chapters: { id: number; title: string }[];
};

type UniversityQueryResult = {
  universities: { id: number; name: string }[];
  truncated: boolean;
};

// select2 4.1 は選択済みタグの × ボタンと検索欄に title / aria-label を付ける。
// language を渡すと、渡していない文言は英語（Remove item など）になるので、どの初期化にも日本語を足す
const commonLanguage = {
  removeAllItems: () => 'すべて解除',
  removeItem: () => '解除',
  search: () => '検索',
};

const select2 = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  const maxSelection = $elem.data('maxSelection');
  const options = {
    width: '100%',
    language: {
      ...commonLanguage,
      noResults: () => '候補が見つかりません',
      maximumSelected: (args: Select2.TranslationArg) => `${args.maximum}件しか選択できません`,
    },
    maximumSelectionLength: maxSelection === null || maxSelection === undefined ? undefined : maxSelection,
  };
  $elem.select2(options);
};
const select2Company = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  const withUnpublished = $elem.data('withUnpublished');
  const unselectableIds = new Set(
    String($elem.data('unselectableIds') ?? '')
      .split(',')
      .filter(Boolean),
  );
  const unselectableLabel: string | undefined = $elem.data('unselectableLabel');
  const noResultsText: string = $elem.data('noResultsText') ?? '候補が見つかりません';
  if ($elem.data('requireSelectionToSubmit')) {
    const $submit = $elem.closest('form').find('[type="submit"]');
    const syncSubmit = () => {
      const unselected = !$elem.val();
      $submit.prop('disabled', unselected).toggleClass('is-disabled', unselected);
    };
    syncSubmit();
    $elem.on('change', syncSubmit);
  }
  $elem.select2({
    debug: true,
    minimumInputLength: 1,
    width: '100%',
    language: {
      ...commonLanguage,
      noResults: () => noResultsText,
      searching: () => '検索中...',
      inputTooShort: (args: Select2.TranslationArg) => `${args.minimum}文字以上入力してください`,
      errorLoading: () => '読み込みに失敗しました。入力し直してください。',
    },
    ajax: {
      delay: 250,
      url: $elem.data('url'),
      data(params: Select2.QueryOptions) {
        const opts: CompanyQueryParam = {
          keyword: params.term!, // ここで params.term が undefined になることはなさそう
        };
        if (withUnpublished) {
          opts.with_unpublished = true;
        }
        return opts;
      },
      processResults(data: CompanyQueryResult, _params: Select2.QueryOptions) {
        if (unselectableIds.size === 0) {
          return { results: data.companies };
        }
        return {
          results: data.companies.map((company) =>
            unselectableIds.has(String(company.id)) ? { ...company, disabled: true } : company,
          ),
        };
      },
    },
    // item は processResults が返した候補がそのまま渡り、id は autocomplete の応答どおり数値で来る。
    // 型は select2 の定義に合わせて string なので、比較の前に文字列に揃える
    templateResult(item: Select2.OptionData) {
      if (!unselectableLabel || !unselectableIds.has(String(item.id))) {
        return item.text;
      }
      return $('<span>').text(item.text).append($('<span class="select2-unselectable-label">').text(unselectableLabel));
    },
  });
};

// 在学中の学校（大学・短大・高専・専門学校）の候補は 3,000 件以上あり、<option> として全件描画すると
// ページが重くなる。初期状態では選択済みの1件だけを描画し、入力されてはじめてサーバから候補を取得する。
// NOTE: クラス名に js-search-select-university を使ってはいけない。company_statistic_contents.coffee が
// そのクラスに対して select2:select で OC.select2（ajaxなし）を張り直すため、1回選ぶとローカル絞り込みに化ける
const select2School = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  $elem.select2({
    width: '100%',
    minimumInputLength: 1,
    language: {
      ...commonLanguage,
      noResults: () => '候補が見つかりません',
      searching: () => '検索中...',
      inputTooShort: () => '学校名を入力してください',
      errorLoading: () => '読み込みに失敗しました。入力し直してください。',
    },
    ajax: {
      delay: 250,
      url: $elem.data('url'),
      data(params: Select2.QueryOptions) {
        return { keyword: params.term == null ? '' : params.term };
      },
      processResults(data: UniversityQueryResult) {
        const results: { id: number | string; text: string; disabled?: boolean }[] = data.universities.map(
          (university) => ({ id: university.id, text: university.name }),
        );
        if (data.truncated) {
          // 上限を超えた分は返していないので、選べない行で絞り込みを促す
          results.push({ id: '', text: '他にも候補があります。学校名をさらに入力してください', disabled: true });
        }
        return { results };
      },
    },
  });
};

export const ADMINS_TRUNCATED_MESSAGE = '他にも候補があります。キーワードをさらに入力してください';

export type AdminsAjaxSelect2Options = {
  // 候補取得 API の URL（select 自体の data-url ではなく呼び出し側が渡す。汎用部品では data-url が外側の要素に付いているため）
  url: string;
  // API レスポンス中の候補配列のキー（例: events, universities）
  collectionKey: string;
  // 候補 1 件を select2 の { id, text } に変換する
  toOption: (item: Record<string, unknown>) => { id: string | number; text: string };
  // 1 文字も入力されていないときの案内
  inputTooShort: string;
  // API に常に付けるクエリ
  extraParams?: Record<string, string>;
  // 既定値（single のときは allowClear など）に上書きする select2 オプション
  select2Options?: Record<string, unknown>;
};

// 管理画面の候補選択（/api/v3/admins/options/*）向けの select2 共通設定。
// 候補は keyword で都度取得し、上限で打ち切られた（truncated）ときは選べない行で絞り込みを促す
export const adminsAjaxSelect2 = ($elem: ReturnType<typeof $>, options: AdminsAjaxSelect2Options) => {
  $elem.select2({
    width: '100%',
    minimumInputLength: 1,
    placeholder: $elem.data('placeholder'),
    language: {
      ...commonLanguage,
      noResults: () => '候補が見つかりません',
      searching: () => '検索中...',
      inputTooShort: () => options.inputTooShort,
      errorLoading: () => '読み込みに失敗しました。入力し直してください。',
    },
    ajax: {
      delay: 250,
      url: options.url,
      data(params: Select2.QueryOptions) {
        return { ...(options.extraParams || {}), keyword: params.term == null ? '' : params.term };
      },
      processResults(data: Record<string, unknown>) {
        const list = data[options.collectionKey];
        const results: { id: string | number; text: string; disabled?: boolean }[] = (
          Array.isArray(list) ? list : []
        ).map((item) => options.toOption(item as Record<string, unknown>));
        if (data.truncated === true) {
          results.push({ id: '', text: ADMINS_TRUNCATED_MESSAGE, disabled: true });
        }
        return { results };
      },
    },
    ...(options.select2Options || {}),
  });
};

// 管理画面のイベント選択。候補は 28,000 件超あるので全件を <option> にせず入力に応じて取得する。
// 表示は AdminsHelper#admins_event_option_label と同じ「【ID】タイトル」
const select2AdminsEvent = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  adminsAjaxSelect2($elem, {
    url: $elem.data('url'),
    collectionKey: 'events',
    toOption: (event) => ({ id: event.id as number, text: `【${event.id}】${event.title}` }),
    inputTooShort: 'イベント名または ID を入力してください',
  });
};

// 管理画面のパートナー選択。候補は 9,000 件超。data-value-key が company_id のときは企業IDを値にする
const select2AdminsPartner = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  const valueKey: 'id' | 'company_id' = $elem.data('valueKey') === 'company_id' ? 'company_id' : 'id';
  adminsAjaxSelect2($elem, {
    url: $elem.data('url'),
    collectionKey: 'partners',
    toOption: (partner) => ({ id: partner[valueKey] as number, text: String(partner.company_name) }),
    inputTooShort: '企業名または ID を入力してください',
    select2Options: { allowClear: $elem.find('option[value=""]').length > 0 },
  });
};

const select2PromotionContract = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  $elem.select2({
    debug: true,
    width: '100%',
    language: {
      ...commonLanguage,
      noResults: () => '候補が見つかりません',
    },
  });
};

const select2VideoChapter = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  $elem.select2({
    debug: true,
    width: '100%',
    language: {
      ...commonLanguage,
      noResults: () => '候補が見つかりません',
      searching: () => '検索中...',
      errorLoading: () => '読み込みに失敗しました。入力し直してください。',
    },
    ajax: {
      delay: 250,
      url: $elem.data('url'),
      data(params: Select2.QueryOptions) {
        const opts: videoBundleCompanyQueryParam = {
          company_ids: $elem.attr('data-company-ids'),
          keyword: params.term!,
        };
        return opts;
      },
      processResults(data: VideoChaptersResult) {
        return { results: data.video_chapters };
      },
    },
  });
};

window.OC = window.OC || {};
window.OC.select2 = select2;

$(document).on('nested:fieldAdded', (e: any) => {
  e.field.find('.js-search-select').each((_: number, elem: HTMLElement) => select2(elem));
  e.field.find('.js-search-select-company').each((_: number, elem: HTMLElement) => select2Company(elem));
  e.field.find('.js-search-select-school').each((_: number, elem: HTMLElement) => select2School(elem));
  e.field
    .find('.js-search-select-promotion-contract')
    .each((_: number, elem: HTMLElement) => select2PromotionContract(elem));
  e.field.find('.js-search-select-video-chapter').each((_: number, elem: HTMLElement) => select2VideoChapter(elem));
  e.field.find('.js-admins-event-select').each((_: number, elem: HTMLElement) => select2AdminsEvent(elem));
  e.field.find('.js-admins-partner-select').each((_: number, elem: HTMLElement) => select2AdminsPartner(elem));
});

window.addEventListener('DOMContentLoaded', () => {
  select2('.js-search-select');
  select2Company('.js-search-select-company');
  select2School('.js-search-select-school');
  select2PromotionContract('.js-search-select-promotion-contract');
  select2VideoChapter('.js-search-select-video-chapter');
  select2AdminsEvent('.js-admins-event-select');
  select2AdminsPartner('.js-admins-partner-select');
});
